import axios from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api'

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add auth token and API key
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // Add API key if available from environment
    const apiKey = import.meta.env.VITE_API_KEY
    if (apiKey) {
      config.headers['X-API-Key'] = apiKey
    }
    
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Track if we're currently refreshing to avoid multiple refresh attempts
let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

// Response interceptor to handle errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Don't retry refresh token endpoint if it fails (to avoid infinite loop)
    if (originalRequest.url?.includes('/auth/refresh-token')) {
      return Promise.reject(error)
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          })
          .catch(err => {
            return Promise.reject(err)
          })
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        console.log('Token expired, attempting to refresh...')
        // Try to refresh the token
        const { authAPI } = await import('./authAPI')
        const response = await authAPI.refreshToken()
        
        if (response.data.success && response.data.data.token) {
          const newToken = response.data.data.token
          localStorage.setItem('token', newToken)
          console.log('Token refreshed successfully via interceptor')
          
          // Update the original request with new token
          originalRequest.headers.Authorization = `Bearer ${newToken}`
          
          // Process queued requests
          processQueue(null, newToken)
          isRefreshing = false
          
          // Retry the original request
          return api(originalRequest)
        } else {
          throw new Error('Token refresh failed: invalid response')
        }
      } catch (refreshError) {
        console.error('Token refresh failed in interceptor:', refreshError)
        // Refresh failed, clear token and redirect to login
        processQueue(refreshError, null)
        isRefreshing = false
        localStorage.removeItem('token')
        
        // Only redirect if not already on login page
        if (window.location.pathname !== '/login') {
          window.location.href = '/login'
        }
        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  register: (userData) => api.post('/auth/register', userData),
  logout: () => api.post('/auth/logout'),
  getCurrentUser: () => api.get('/auth/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  refreshToken: () => api.post('/auth/refresh-token'),
}

// Users API
export const usersAPI = {
  getAll: (params) => api.get('/users', { params }),
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  updateStatus: (id, status) => api.patch(`/users/${id}/status`, { status }),
  delete: (id) => api.delete(`/users/${id}`),
  getControlCenter: (params) => api.get('/users/control-center', { params }),
  getByOrganization: (orgId, params) => api.get(`/users/organization/${orgId}`, { params }),
  getStats: () => api.get('/users/stats/overview'),
}

// Organizations API
export const organizationsAPI = {
  getAll: (params) => api.get('/organizations', { params }),
  getById: (id) => api.get(`/organizations/${id}`),
  create: (data) => api.post('/organizations', data),
  update: (id, data) => api.put(`/organizations/${id}`, data),
  updateStatus: (id, status) => api.patch(`/organizations/${id}/status`, { status }),
  updateBranding: (id, data) => {
    if (typeof FormData !== 'undefined' && data instanceof FormData) {
      return api.patch(`/organizations/${id}/branding`, data, {
        headers: { 'Content-Type': undefined }
      })
    }
    return api.patch(`/organizations/${id}/branding`, data)
  },
  delete: (id) => api.delete(`/organizations/${id}`),
  search: (params) => api.get('/organizations/search', { params }),
  getStats: () => api.get('/organizations/stats'),
}

// Roles API
export const rolesAPI = {
  getAll: (params) => api.get('/roles', { params }),
  getById: (id) => api.get(`/roles/${id}`),
  create: (data) => api.post('/roles', data),
  update: (id, data) => api.put(`/roles/${id}`, data),
  delete: (id) => api.delete(`/roles/${id}`),
  addPermission: (id, permission) => api.post(`/roles/${id}/permissions`, { permission }),
  removePermission: (id, permission) => api.delete(`/roles/${id}/permissions`, { data: { permission } }),
  getPermissions: () => api.get('/roles/permissions'),
  initializeDefaults: () => api.post('/roles/initialize-defaults'),
  getStats: () => api.get('/roles/stats'),
}

// Plans API
export const plansAPI = {
  getByProduct: (productId) => api.get(`/plans/product/${productId}`),
  getById: (id) => api.get(`/plans/${id}`),
  create: (data) => api.post('/plans', data),
  update: (id, data) => api.put(`/plans/${id}`, data),
  delete: (id) => api.delete(`/plans/${id}`),
}

// Payments API
export const paymentsAPI = {
  getById: (id) => api.get(`/payments/${id}`),
  getBySubscription: (subscriptionId) => api.get(`/payments/subscription/${subscriptionId}`),
  getByOrganization: (organizationId) => api.get(`/payments/organization/${organizationId}`),
  create: (data) => api.post('/payments', data),
  update: (id, data) => api.put(`/payments/${id}`, data),
  delete: (id) => api.delete(`/payments/${id}`),
}

// System API
export const systemAPI = {
  getStatus: () => api.get('/system/status'),
}

export default api



